/* ============================================================
   Procedural texture factory (canvas based, cached)
   - Seeded periodic value-noise (seamless around the X axis so
     equirectangular planet maps have no visible seam)
   - Planet surfaces by type (lava / desert / gas / ice / rocky)
   - Earth albedo + roughness + cloud layer
   - Planet rings, national flags, solar cells, glass facades,
     airliner livery, concrete/granite
   All generators are lazy and memoised so the cost is paid once.
   ============================================================ */
import * as THREE from "three";

const HAS_DOM = typeof document !== "undefined";
const cache = new Map();

function memo(key, fn) {
  if (cache.has(key)) return cache.get(key);
  const v = fn();
  cache.set(key, v);
  return v;
}

function makeCanvas(w, h) {
  const c = document.createElement("canvas");
  c.width = w;
  c.height = h;
  return c;
}

function toTexture(canvas, { srgb = true, repeat = false, aniso = 4 } = {}) {
  const t = new THREE.CanvasTexture(canvas);
  if (srgb) t.colorSpace = THREE.SRGBColorSpace;
  if (repeat) {
    t.wrapS = THREE.RepeatWrapping;
    t.wrapT = THREE.RepeatWrapping;
  } else {
    t.wrapS = THREE.RepeatWrapping; // horizontal wrap is always safe for equirect maps
    t.wrapT = THREE.ClampToEdgeWrapping;
  }
  t.anisotropy = aniso;
  t.needsUpdate = true;
  return t;
}

/* ---------------- Noise ---------------- */
export function makeNoise2D(seed = 1) {
  const perm = new Uint8Array(512);
  const p = new Array(256);
  for (let i = 0; i < 256; i++) p[i] = i;
  let s = (Math.abs(Math.floor(seed * 1013)) % 2147483646) + 1;
  const rnd = () => {
    s = (s * 16807) % 2147483647;
    return (s - 1) / 2147483646;
  };
  for (let i = 255; i > 0; i--) {
    const j = Math.floor(rnd() * (i + 1));
    const t = p[i];
    p[i] = p[j];
    p[j] = t;
  }
  for (let i = 0; i < 512; i++) perm[i] = p[i & 255];
  const fade = (t) => t * t * (3 - 2 * t);
  // period: integer number of lattice cells along X before wrapping (<= 256)
  const noise = (x, y, period = 256) => {
    const xi = Math.floor(x);
    const yi = Math.floor(y);
    const xf = x - xi;
    const yf = y - yi;
    const X0 = (((xi % period) + period) % period) & 255;
    const X1 = ((X0 + 1) % period) & 255;
    const Y0 = yi & 255;
    const Y1 = (yi + 1) & 255;
    const a = perm[X0 + perm[Y0]] / 255;
    const b = perm[X1 + perm[Y0]] / 255;
    const c = perm[X0 + perm[Y1]] / 255;
    const d = perm[X1 + perm[Y1]] / 255;
    const u = fade(xf);
    const v = fade(yf);
    return (a + (b - a) * u) * (1 - v) + (c + (d - c) * u) * v;
  };
  // fractal brownian motion, x in [0, period)
  const fbm = (x, y, period, octaves = 5, gain = 0.5) => {
    let amp = 0.5;
    let sum = 0;
    let norm = 0;
    let f = 1;
    for (let o = 0; o < octaves; o++) {
      const per = Math.min(256, period * f);
      sum += noise(x * f, y * f, per) * amp;
      norm += amp;
      amp *= gain;
      f *= 2;
    }
    return sum / norm;
  };
  const ridged = (x, y, period, octaves = 4) => {
    let amp = 0.5;
    let sum = 0;
    let norm = 0;
    let f = 1;
    for (let o = 0; o < octaves; o++) {
      const per = Math.min(256, period * f);
      const n = 1 - Math.abs(noise(x * f, y * f, per) * 2 - 1);
      sum += n * n * amp;
      norm += amp;
      amp *= 0.5;
      f *= 2;
    }
    return sum / norm;
  };
  return { noise, fbm, ridged, rnd };
}

/* 3D value noise for mesh displacement (asteroids) */
export function makeNoise3D(seed = 7) {
  const perm = new Uint8Array(512);
  const p = new Array(256);
  for (let i = 0; i < 256; i++) p[i] = i;
  let s = (Math.abs(Math.floor(seed * 7919)) % 2147483646) + 1;
  for (let i = 255; i > 0; i--) {
    s = (s * 16807) % 2147483647;
    const j = Math.floor(((s - 1) / 2147483646) * (i + 1));
    const t = p[i];
    p[i] = p[j];
    p[j] = t;
  }
  for (let i = 0; i < 512; i++) perm[i] = p[i & 255];
  const fade = (t) => t * t * (3 - 2 * t);
  const g = (X, Y, Z) => perm[(X + perm[(Y + perm[Z & 255]) & 255]) & 255] / 255;
  const noise = (x, y, z) => {
    const xi = Math.floor(x), yi = Math.floor(y), zi = Math.floor(z);
    const xf = x - xi, yf = y - yi, zf = z - zi;
    const u = fade(xf), v = fade(yf), w = fade(zf);
    const X = xi & 255, Y = yi & 255, Z = zi & 255;
    const lerp = (a, b, t) => a + (b - a) * t;
    const x00 = lerp(g(X, Y, Z), g(X + 1, Y, Z), u);
    const x10 = lerp(g(X, Y + 1, Z), g(X + 1, Y + 1, Z), u);
    const x01 = lerp(g(X, Y, Z + 1), g(X + 1, Y, Z + 1), u);
    const x11 = lerp(g(X, Y + 1, Z + 1), g(X + 1, Y + 1, Z + 1), u);
    return lerp(lerp(x00, x10, v), lerp(x01, x11, v), w);
  };
  const fbm = (x, y, z, octaves = 4) => {
    let amp = 0.5, sum = 0, norm = 0, f = 1;
    for (let o = 0; o < octaves; o++) {
      sum += noise(x * f, y * f, z * f) * amp;
      norm += amp;
      amp *= 0.5;
      f *= 2;
    }
    return sum / norm;
  };
  return { noise, fbm };
}

/* ---------------- Colour helpers ---------------- */
function hexToRgb(hex) {
  const h = hex.replace("#", "");
  const n = parseInt(h.length === 3 ? h.split("").map((c) => c + c).join("") : h, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
function mix(a, b, t) {
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
}
function shade(rgb, k) {
  return [clamp255(rgb[0] * k), clamp255(rgb[1] * k), clamp255(rgb[2] * k)];
}
function clamp255(v) {
  return v < 0 ? 0 : v > 255 ? 255 : v;
}
function clamp01(v) {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}
function smooth(a, b, x) {
  const t = clamp01((x - a) / (b - a));
  return t * t * (3 - 2 * t);
}

/* Draw a crater (rim highlight + dark floor) on an rgb canvas ctx and a height ctx */
function drawCrater(ctx, hctx, cx, cy, r, base, depth = 1) {
  // floor
  const g = ctx.createRadialGradient(cx - r * 0.15, cy - r * 0.15, r * 0.05, cx, cy, r);
  const f = shade(base, 0.55);
  const rim = shade(base, 1.14);
  const shadow = shade(base, 0.7);
  g.addColorStop(0, `rgba(${f[0] | 0},${f[1] | 0},${f[2] | 0},0.85)`);
  g.addColorStop(0.55, `rgba(${shadow[0] | 0},${shadow[1] | 0},${shadow[2] | 0},0.55)`);
  g.addColorStop(0.8, `rgba(${rim[0] | 0},${rim[1] | 0},${rim[2] | 0},0.45)`);
  g.addColorStop(0.92, `rgba(${rim[0] | 0},${rim[1] | 0},${rim[2] | 0},0.25)`);
  g.addColorStop(1, `rgba(${rim[0] | 0},${rim[1] | 0},${rim[2] | 0},0)`);
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fill();
  if (hctx) {
    const hg = hctx.createRadialGradient(cx, cy, r * 0.1, cx, cy, r);
    hg.addColorStop(0, `rgba(0,0,0,${0.7 * depth})`);
    hg.addColorStop(0.72, `rgba(0,0,0,${0.35 * depth})`);
    hg.addColorStop(0.86, `rgba(255,255,255,${0.75 * depth})`);
    hg.addColorStop(1, "rgba(255,255,255,0)");
    hctx.fillStyle = hg;
    hctx.beginPath();
    hctx.arc(cx, cy, r, 0, Math.PI * 2);
    hctx.fill();
  }
}

/* ============================================================
   PLANET SURFACES
   type: "lava" | "desert" | "gas" | "ice" | "rocky"
   Returns { map, bumpMap, emissiveMap? }
   ============================================================ */
export function getPlanetTextures(type, baseHex, seed = 1, W = 512, H = 256) {
  return memo(`planet-${type}-${baseHex}-${seed}-${W}`, () => {
    if (!HAS_DOM) return {};
    const { fbm, ridged, noise, rnd } = makeNoise2D(seed);
    const base = hexToRgb(baseHex);
    const canvas = makeCanvas(W, H);
    const ctx = canvas.getContext("2d");
    const img = ctx.createImageData(W, H);
    const hCanvas = makeCanvas(W, H);
    const hctx = hCanvas.getContext("2d");
    const himg = hctx.createImageData(W, H);
    let eCanvas = null;
    let eimg = null;
    let ectx = null;
    if (type === "lava") {
      eCanvas = makeCanvas(W, H);
      ectx = eCanvas.getContext("2d");
      eimg = ectx.createImageData(W, H);
    }
    const P = 6; // base period (cells across the map)
    const d = img.data;
    const hd = himg.data;
    const ed = eimg ? eimg.data : null;

    for (let py = 0; py < H; py++) {
      const v = py / H;
      const lat = (v - 0.5) * Math.PI; // -pi/2..pi/2
      // squash X near poles so features don't stretch as much
      const polar = Math.cos(lat);
      for (let px = 0; px < W; px++) {
        const u = px / W;
        const x = u * P;
        const y = v * P * (H / W) * 2;
        const i = (py * W + px) * 4;
        let rgb;
        let h = 0.5;
        let em = 0;

        if (type === "gas") {
          // latitudinal bands distorted by turbulence
          const turb = fbm(x * 1.5, y * 1.5, P * 1.5, 4) - 0.5;
          const band = Math.sin((v + turb * 0.09) * Math.PI * 14) * 0.5 + 0.5;
          const band2 = Math.sin((v + turb * 0.05) * Math.PI * 5 + 1.3) * 0.5 + 0.5;
          const storm = fbm(x * 4, y * 4, P * 4, 3);
          const light = shade(base, 1.35);
          const dark = shade(base, 0.55);
          const cream = mix(shade(base, 1.2), [240, 228, 210], 0.45);
          rgb = mix(dark, light, band);
          rgb = mix(rgb, cream, band2 * 0.35);
          rgb = mix(rgb, shade(base, 0.8), smooth(0.62, 0.8, storm) * 0.6);
          h = 0.5 + (band - 0.5) * 0.15;
        } else if (type === "lava") {
          const rock = fbm(x, y, P, 5);
          const cracks = ridged(x * 2, y * 2, P * 2, 4);
          const basalt = mix([28, 22, 24], [70, 58, 56], rock);
          const hot = mix([255, 120, 30], [255, 220, 120], clamp01((cracks - 0.55) * 3));
          const crackMask = smooth(0.5, 0.78, cracks) * (0.4 + 0.6 * smooth(0.3, 0.7, rock));
          rgb = mix(basalt, hot, crackMask);
          rgb = mix(rgb, base, 0.08);
          h = rock * 0.7 + (1 - crackMask) * 0.3;
          em = crackMask;
        } else if (type === "ice") {
          const n = fbm(x, y, P, 5);
          const cr = ridged(x * 3, y * 3, P * 3, 4);
          const white = [232, 244, 250];
          const tint = mix(base, white, 0.55);
          const deep = shade(base, 0.7);
          rgb = mix(deep, tint, smooth(0.35, 0.7, n));
          rgb = mix(rgb, white, smooth(0.6, 0.9, n) * 0.6);
          // dark fractures
          const frac = smooth(0.74, 0.92, cr);
          rgb = mix(rgb, shade(base, 0.45), frac * 0.8);
          // polar caps
          const cap = smooth(0.62, 0.9, Math.abs(lat) / (Math.PI / 2));
          rgb = mix(rgb, white, cap);
          h = n * 0.6 + (1 - frac) * 0.4;
        } else if (type === "desert") {
          const n = fbm(x, y, P, 5);
          const dunes = Math.sin((x * 6 + fbm(x * 2, y * 2, P * 2, 3) * 3) * polar) * 0.5 + 0.5;
          const dark = shade(base, 0.55);
          const light = mix(shade(base, 1.25), [245, 230, 205], 0.25);
          rgb = mix(dark, light, n * 0.7 + dunes * 0.3);
          // dark maria patches
          const maria = fbm(x * 0.5 + 3, y * 0.5 + 1, P * 0.5, 3);
          rgb = mix(rgb, shade(base, 0.6), smooth(0.58, 0.72, maria) * 0.55);
          // polar frost
          const cap = smooth(0.8, 0.97, Math.abs(lat) / (Math.PI / 2));
          rgb = mix(rgb, [235, 230, 225], cap * 0.85);
          h = n * 0.75 + dunes * 0.25;
        } else {
          // rocky / moon
          const n = fbm(x, y, P, 6);
          const grey = mix(base, [150, 148, 145], 0.55);
          const dark = shade(grey, 0.55);
          const light = shade(grey, 1.15);
          rgb = mix(dark, light, n);
          const maria = fbm(x * 0.5 + 7, y * 0.5 + 2, P * 0.5, 3);
          rgb = mix(rgb, shade(grey, 0.62), smooth(0.56, 0.7, maria) * 0.7);
          h = n;
        }
        // fine grain
        const grain = (noise(px * 0.9, py * 0.9, 256) - 0.5) * 12;
        d[i] = clamp255(rgb[0] + grain);
        d[i + 1] = clamp255(rgb[1] + grain);
        d[i + 2] = clamp255(rgb[2] + grain);
        d[i + 3] = 255;
        const hv = clamp255(h * 255);
        hd[i] = hv;
        hd[i + 1] = hv;
        hd[i + 2] = hv;
        hd[i + 3] = 255;
        if (ed) {
          const ev = clamp255(em * 255);
          ed[i] = ev;
          ed[i + 1] = clamp255(ev * 0.55);
          ed[i + 2] = clamp255(ev * 0.15);
          ed[i + 3] = 255;
        }
      }
    }
    ctx.putImageData(img, 0, 0);
    hctx.putImageData(himg, 0, 0);
    if (ectx) ectx.putImageData(eimg, 0, 0);

    // craters for rocky & desert worlds
    if (type === "rocky" || type === "desert") {
      const count = type === "rocky" ? 110 : 40;
      const grey = type === "rocky" ? mix(base, [150, 148, 145], 0.55) : base;
      for (let k = 0; k < count; k++) {
        const cx = rnd() * W;
        const cy = H * 0.08 + rnd() * H * 0.84;
        const r = 2 + Math.pow(rnd(), 2.6) * (type === "rocky" ? 22 : 14);
        drawCrater(ctx, hctx, cx, cy, r, grey, 1);
        // wrap duplicates near the seam
        if (cx < r) drawCrater(ctx, hctx, cx + W, cy, r, grey, 1);
        if (cx > W - r) drawCrater(ctx, hctx, cx - W, cy, r, grey, 1);
      }
    }
    // volcanic vents for lava
    if (type === "lava" && ectx) {
      for (let k = 0; k < 9; k++) {
        const cx = rnd() * W;
        const cy = H * 0.15 + rnd() * H * 0.7;
        const r = 8 + rnd() * 18;
        const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
        g.addColorStop(0, "rgba(255,190,90,0.95)");
        g.addColorStop(0.35, "rgba(255,110,30,0.8)");
        g.addColorStop(1, "rgba(40,20,20,0)");
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(cx, cy, r, 0, Math.PI * 2);
        ctx.fill();
        const eg = ectx.createRadialGradient(cx, cy, 0, cx, cy, r);
        eg.addColorStop(0, "rgba(255,200,110,1)");
        eg.addColorStop(0.4, "rgba(255,110,30,0.8)");
        eg.addColorStop(1, "rgba(0,0,0,0)");
        ectx.fillStyle = eg;
        ectx.beginPath();
        ectx.arc(cx, cy, r, 0, Math.PI * 2);
        ectx.fill();
      }
    }
    const out = {
      map: toTexture(canvas),
      bumpMap: toTexture(hCanvas, { srgb: false }),
    };
    if (eCanvas) out.emissiveMap = toTexture(eCanvas);
    return out;
  });
}

/* ============================================================
   EARTH  (albedo + roughness + clouds)
   ============================================================ */
export function getEarthTextures(W = 768, H = 384) {
  return memo(`earth-${W}`, () => {
    if (!HAS_DOM) return {};
    const { fbm, noise } = makeNoise2D(42);
    const canvas = makeCanvas(W, H);
    const ctx = canvas.getContext("2d");
    const img = ctx.createImageData(W, H);
    const rCanvas = makeCanvas(W, H);
    const rctx = rCanvas.getContext("2d");
    const rimg = rctx.createImageData(W, H);
    const d = img.data;
    const rd = rimg.data;
    const P = 5;
    const deepOcean = [8, 38, 82];
    const shallow = [22, 96, 140];
    const coast = [70, 150, 165];
    const sand = [214, 196, 150];
    const grass = [62, 118, 52];
    const forest = [28, 78, 40];
    const desert = [196, 160, 96];
    const tundra = [140, 136, 118];
    const snow = [240, 246, 250];
    for (let py = 0; py < H; py++) {
      const v = py / H;
      const lat = Math.abs((v - 0.5) * 2); // 0 equator .. 1 pole
      for (let px = 0; px < W; px++) {
        const u = px / W;
        const x = u * P;
        const y = v * P * 0.5 * 2;
        const i = (py * W + px) * 4;
        const cont = fbm(x, y, P, 6, 0.52);
        const detail = fbm(x * 3 + 11, y * 3 + 5, P * 3, 4);
        // land threshold with polar bias (more land toward poles = ice caps)
        const landV = cont + lat * lat * 0.22;
        const isLand = landV > 0.56;
        let rgb;
        let rough = 0.35;
        if (!isLand) {
          const depth = smooth(0.56, 0.3, landV);
          rgb = mix(coast, mix(shallow, deepOcean, depth), smooth(0.0, 0.12, 0.56 - landV) * 0.75 + 0.25);
          rough = 0.28 + (1 - depth) * 0.1;
          if (lat > 0.9) {
            rgb = mix(rgb, snow, smooth(0.9, 0.97, lat));
            rough = 0.7;
          }
        } else {
          const elev = smooth(0.56, 0.75, landV);
          const climate = fbm(x * 2 + 21, y * 2 + 9, P * 2, 3);
          rgb = mix(sand, grass, smooth(0.0, 0.06, landV - 0.56));
          rgb = mix(rgb, forest, smooth(0.45, 0.7, detail) * (1 - lat) * 0.8);
          // deserts around the tropics
          const tropic = Math.exp(-Math.pow((lat - 0.3) / 0.13, 2));
          rgb = mix(rgb, desert, tropic * smooth(0.45, 0.65, climate) * 0.9);
          // tundra & snow toward poles / high elevation
          rgb = mix(rgb, tundra, smooth(0.62, 0.8, lat));
          rgb = mix(rgb, snow, smooth(0.78, 0.92, lat) + elev * smooth(0.7, 0.95, detail) * 0.7);
          rough = 0.9;
        }
        const grain = (noise(px * 0.8, py * 0.8, 256) - 0.5) * 8;
        d[i] = clamp255(rgb[0] + grain);
        d[i + 1] = clamp255(rgb[1] + grain);
        d[i + 2] = clamp255(rgb[2] + grain);
        d[i + 3] = 255;
        const rv = clamp255(rough * 255);
        rd[i] = rv;
        rd[i + 1] = rv;
        rd[i + 2] = rv;
        rd[i + 3] = 255;
      }
    }
    ctx.putImageData(img, 0, 0);
    rctx.putImageData(rimg, 0, 0);
    return {
      map: toTexture(canvas),
      roughnessMap: toTexture(rCanvas, { srgb: false }),
      clouds: getCloudTexture(),
    };
  });
}

export function getCloudTexture(W = 512, H = 256) {
  return memo(`clouds-${W}`, () => {
    if (!HAS_DOM) return null;
    const { fbm } = makeNoise2D(99);
    const canvas = makeCanvas(W, H);
    const ctx = canvas.getContext("2d");
    const img = ctx.createImageData(W, H);
    const d = img.data;
    const P = 6;
    for (let py = 0; py < H; py++) {
      const v = py / H;
      for (let px = 0; px < W; px++) {
        const u = px / W;
        const n = fbm(u * P, v * P, P, 5, 0.55);
        const swirl = fbm(u * P * 2 + 3, v * P * 2 + 1, P * 2, 3);
        const a = smooth(0.5, 0.72, n * 0.75 + swirl * 0.25);
        const i = (py * W + px) * 4;
        d[i] = 255;
        d[i + 1] = 255;
        d[i + 2] = 255;
        d[i + 3] = clamp255(a * 235);
      }
    }
    ctx.putImageData(img, 0, 0);
    return toTexture(canvas);
  });
}

/* ============================================================
   PLANET RING  (concentric bands with alpha) – mapped on RingGeometry
   ============================================================ */
export function getRingTexture(baseHex, seed = 3) {
  return memo(`ring-${baseHex}-${seed}`, () => {
    if (!HAS_DOM) return null;
    const S = 512;
    const canvas = makeCanvas(S, S);
    const ctx = canvas.getContext("2d");
    const { noise } = makeNoise2D(seed);
    const base = hexToRgb(baseHex);
    const c = S / 2;
    const steps = 160;
    for (let k = 0; k < steps; k++) {
      const f = k / steps; // 0 inner .. 1 outer
      const r0 = c * f;
      const r1 = c * (f + 1 / steps) + 0.6;
      const n = noise(k * 0.35, 1.3, 256);
      const n2 = noise(k * 1.7, 8.1, 256);
      const gapFactor = smooth(0.25, 0.6, n2); // dark gaps
      const alpha = clamp01((0.15 + n * 0.75) * gapFactor * smooth(0.0, 0.08, f) * smooth(1.0, 0.9, f));
      const tone = mix(shade(base, 0.7), mix(base, [235, 225, 210], 0.55), n);
      ctx.fillStyle = `rgba(${tone[0] | 0},${tone[1] | 0},${tone[2] | 0},${alpha})`;
      ctx.beginPath();
      ctx.arc(c, c, r1, 0, Math.PI * 2);
      ctx.arc(c, c, r0, 0, Math.PI * 2, true);
      ctx.fill();
    }
    const t = toTexture(canvas);
    t.wrapS = THREE.ClampToEdgeWrapping;
    return t;
  });
}

/* ============================================================
   FLAGS – accurate vector drawings
   kind: "china" | "hongkong" | "france" | "usa" | "greinch"
   ============================================================ */
function drawStar(ctx, cx, cy, rOuter, rInner, rotation, color) {
  ctx.fillStyle = color;
  ctx.beginPath();
  for (let i = 0; i < 10; i++) {
    const r = i % 2 === 0 ? rOuter : rInner;
    const a = rotation + (i * Math.PI) / 5 - Math.PI / 2;
    const x = cx + Math.cos(a) * r;
    const y = cy + Math.sin(a) * r;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.closePath();
  ctx.fill();
}

export function getFlagTexture(kind) {
  return memo(`flag-${kind}`, () => {
    if (!HAS_DOM) return null;
    let W = 384;
    let H = 256;
    if (kind === "usa") {
      W = 380;
      H = 200;
    }
    const canvas = makeCanvas(W, H);
    const ctx = canvas.getContext("2d");
    if (kind === "china") {
      ctx.fillStyle = "#DE2910";
      ctx.fillRect(0, 0, W, H);
      const ux = W / 30;
      const uy = H / 20;
      const bx = 5 * ux;
      const by = 5 * uy;
      drawStar(ctx, bx, by, 3 * uy, 1.2 * uy, 0, "#FFDE00");
      const smalls = [
        [10, 2],
        [12, 4],
        [12, 7],
        [10, 9],
      ];
      smalls.forEach(([sx, sy]) => {
        const cx = sx * ux;
        const cy = sy * uy;
        const rot = Math.atan2(by - cy, bx - cx) + Math.PI / 2;
        drawStar(ctx, cx, cy, 1 * uy, 0.4 * uy, rot, "#FFDE00");
      });
    } else if (kind === "hongkong") {
      ctx.fillStyle = "#DE2910";
      ctx.fillRect(0, 0, W, H);
      // Bauhinia: 5 white petals in a pinwheel
      const cx = W / 2;
      const cy = H / 2;
      const R = H * 0.3;
      ctx.fillStyle = "#FFFFFF";
      for (let i = 0; i < 5; i++) {
        const a = (i / 5) * Math.PI * 2 - Math.PI / 2;
        ctx.save();
        ctx.translate(cx, cy);
        ctx.rotate(a);
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.bezierCurveTo(R * 0.55, -R * 0.15, R * 1.05, -R * 0.25, R * 0.95, R * 0.05);
        ctx.bezierCurveTo(R * 0.85, R * 0.35, R * 0.4, R * 0.35, 0, 0);
        ctx.fill();
        // red star + stamen line on the petal
        ctx.strokeStyle = "#DE2910";
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(R * 0.15, R * 0.03);
        ctx.quadraticCurveTo(R * 0.45, R * 0.02, R * 0.75, R * 0.04);
        ctx.stroke();
        drawStar(ctx, R * 0.6, R * 0.0, R * 0.09, R * 0.04, 0, "#DE2910");
        ctx.restore();
      }
    } else if (kind === "france") {
      ctx.fillStyle = "#0055A4";
      ctx.fillRect(0, 0, W / 3, H);
      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(W / 3, 0, W / 3, H);
      ctx.fillStyle = "#EF4135";
      ctx.fillRect((2 * W) / 3, 0, W / 3, H);
    } else if (kind === "usa") {
      const stripe = H / 13;
      for (let i = 0; i < 13; i++) {
        ctx.fillStyle = i % 2 === 0 ? "#B22234" : "#FFFFFF";
        ctx.fillRect(0, i * stripe, W, stripe + 0.5);
      }
      const cw = W * 0.4;
      const ch = stripe * 7;
      ctx.fillStyle = "#3C3B6E";
      ctx.fillRect(0, 0, cw, ch);
      // 50 stars: 9 rows alternating 6 / 5
      const rows = 9;
      const rowH = ch / (rows + 1);
      const colW = cw / 12;
      for (let r = 0; r < rows; r++) {
        const n = r % 2 === 0 ? 6 : 5;
        const offset = r % 2 === 0 ? colW : colW * 2;
        for (let c = 0; c < n; c++) {
          const x = offset + c * colW * 2;
          const y = rowH * (r + 1);
          drawStar(ctx, x, y, rowH * 0.42, rowH * 0.18, 0, "#FFFFFF");
        }
      }
    } else {
      // GREINCH corporate flag
      ctx.fillStyle = "#0B0A0C";
      ctx.fillRect(0, 0, W, H);
      ctx.fillStyle = "#E94B4B";
      ctx.fillRect(0, H * 0.42, W, H * 0.16);
      ctx.fillStyle = "#F4EADE";
      ctx.font = `bold ${Math.floor(H * 0.28)}px Montserrat, Arial, sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("GREINCH", W / 2, H * 0.5);
    }
    // subtle fabric weave
    ctx.globalAlpha = 0.07;
    ctx.fillStyle = "#000";
    for (let y = 0; y < H; y += 3) ctx.fillRect(0, y, W, 1);
    ctx.globalAlpha = 1;
    return toTexture(canvas);
  });
}

/* ============================================================
   SOLAR CELLS  (repeatable)
   ============================================================ */
export function getSolarTexture() {
  return memo("solar", () => {
    if (!HAS_DOM) return null;
    const W = 128;
    const H = 256;
    const canvas = makeCanvas(W, H);
    const ctx = canvas.getContext("2d");
    const grad = ctx.createLinearGradient(0, 0, W, H);
    grad.addColorStop(0, "#102A5E");
    grad.addColorStop(0.5, "#0B1E48");
    grad.addColorStop(1, "#13306A");
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, W, H);
    ctx.strokeStyle = "rgba(200,210,225,0.85)";
    ctx.lineWidth = 2;
    const cw = W / 4;
    const chh = H / 8;
    for (let x = 0; x <= W; x += cw) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, H);
      ctx.stroke();
    }
    for (let y = 0; y <= H; y += chh) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(W, y);
      ctx.stroke();
    }
    // cell highlights
    ctx.fillStyle = "rgba(120,160,230,0.18)";
    for (let x = 0; x < W; x += cw) {
      for (let y = 0; y < H; y += chh) {
        ctx.fillRect(x + 2, y + 2, cw * 0.5, chh * 0.35);
      }
    }
    return toTexture(canvas, { repeat: true });
  });
}

/* ============================================================
   GLASS FACADE (repeatable) - window grid with a few lit rooms
   tile = 4 windows wide x 8 floors tall
   ============================================================ */
export function getFacadeTexture(tint = "#1F3348", lit = 0.22, seed = 5) {
  return memo(`facade-${tint}-${lit}-${seed}`, () => {
    if (!HAS_DOM) return null;
    const W = 256;
    const H = 512;
    const canvas = makeCanvas(W, H);
    const ctx = canvas.getContext("2d");
    const { rnd } = makeNoise2D(seed);
    const base = hexToRgb(tint);
    ctx.fillStyle = `rgb(${shade(base, 0.7).map((v) => v | 0).join(",")})`;
    ctx.fillRect(0, 0, W, H);
    const cols = 4;
    const rows = 8;
    const cw = W / cols;
    const rh = H / rows;
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const x = c * cw;
        const y = r * rh;
        const isLit = rnd() < lit;
        const skyRef = 0.9 + rnd() * 0.5;
        const col = isLit
          ? [255, 226, 170]
          : mix(shade(base, skyRef), [170, 205, 230], rnd() * 0.45);
        ctx.fillStyle = `rgb(${col.map((v) => v | 0).join(",")})`;
        ctx.fillRect(x + 4, y + 5, cw - 8, rh - 10);
        // window mullion highlight
        ctx.fillStyle = "rgba(255,255,255,0.12)";
        ctx.fillRect(x + 4, y + 5, cw - 8, 3);
      }
    }
    // floor slabs
    ctx.fillStyle = "rgba(0,0,0,0.28)";
    for (let r = 0; r <= rows; r++) ctx.fillRect(0, r * rh - 2, W, 4);
    return toTexture(canvas, { repeat: true });
  });
}

/* ============================================================
   CONCRETE / GRANITE (repeatable subtle noise)
   ============================================================ */
export function getStoneTexture(hex = "#8E8579", seed = 11, contrast = 22) {
  return memo(`stone-${hex}-${seed}`, () => {
    if (!HAS_DOM) return null;
    const S = 256;
    const canvas = makeCanvas(S, S);
    const ctx = canvas.getContext("2d");
    const img = ctx.createImageData(S, S);
    const { fbm, noise } = makeNoise2D(seed);
    const base = hexToRgb(hex);
    const d = img.data;
    for (let y = 0; y < S; y++) {
      for (let x = 0; x < S; x++) {
        const n = fbm((x / S) * 8, (y / S) * 8, 8, 4);
        const g = (noise(x * 1.3, y * 1.3, 256) - 0.5) * contrast;
        const i = (y * S + x) * 4;
        const k = 0.85 + n * 0.3;
        d[i] = clamp255(base[0] * k + g);
        d[i + 1] = clamp255(base[1] * k + g);
        d[i + 2] = clamp255(base[2] * k + g);
        d[i + 3] = 255;
      }
    }
    ctx.putImageData(img, 0, 0);
    return toTexture(canvas, { repeat: true });
  });
}

/* ============================================================
   AIRLINER LIVERY  – wraps a LatheGeometry fuselage
   u: 0 belly → 0.25 right → 0.5 top → 0.75 left → 1 belly
   v: 0 nose → 1 tail (profile is sampled uniformly in z)
   ============================================================ */
export function getLiveryTexture() {
  return memo("livery", () => {
    if (!HAS_DOM) return null;
    const W = 512; // u (around)
    const H = 1024; // v (along)
    const canvas = makeCanvas(W, H);
    const ctx = canvas.getContext("2d");
    // v=0 is the BOTTOM row of the canvas in texture space → nose at bottom.
    const vy = (v) => (1 - v) * H;
    // white fuselage
    ctx.fillStyle = "#F6F2EC";
    ctx.fillRect(0, 0, W, H);
    // grey belly (u < 0.19 and u > 0.81)
    ctx.fillStyle = "#B9B7B3";
    ctx.fillRect(0, 0, W * 0.19, H);
    ctx.fillRect(W * 0.81, 0, W * 0.19, H);
    // belly shadow gradient toward keel
    const bg = ctx.createLinearGradient(0, 0, W * 0.19, 0);
    bg.addColorStop(0, "rgba(60,60,64,0.55)");
    bg.addColorStop(1, "rgba(60,60,64,0)");
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, W * 0.19, H);
    const bg2 = ctx.createLinearGradient(W, 0, W * 0.81, 0);
    bg2.addColorStop(0, "rgba(60,60,64,0.55)");
    bg2.addColorStop(1, "rgba(60,60,64,0)");
    ctx.fillStyle = bg2;
    ctx.fillRect(W * 0.81, 0, W * 0.19, H);
    // coral cheat lines
    ctx.fillStyle = "#E94B4B";
    ctx.fillRect(W * 0.19, vy(0.97), W * 0.022, vy(0.03) - vy(0.97));
    ctx.fillRect(W * (0.81 - 0.022), vy(0.97), W * 0.022, vy(0.03) - vy(0.97));
    // thin burgundy accent line above cheat line
    ctx.fillStyle = "#6B1F47";
    ctx.fillRect(W * 0.215, vy(0.95), W * 0.006, vy(0.05) - vy(0.95));
    ctx.fillRect(W * (0.785 - 0.006), vy(0.95), W * 0.006, vy(0.05) - vy(0.95));
    // passenger windows (both sides)
    const winU = [0.287, 0.713];
    const winH = W * 0.026; // in u pixels (window height, around)
    const winW = H * 0.0105; // in v pixels (window width, along)
    ctx.fillStyle = "#101418";
    for (let v = 0.235; v < 0.69; v += 0.0215) {
      // door gaps
      if ((v > 0.212 && v < 0.232) || (v > 0.685 && v < 0.705)) continue;
      winU.forEach((u) => {
        const x = u * W - winH / 2;
        const y = vy(v) - winW / 2;
        ctx.beginPath();
        // rounded window
        const r = 4;
        ctx.moveTo(x + r, y);
        ctx.arcTo(x + winH, y, x + winH, y + winW, r);
        ctx.arcTo(x + winH, y + winW, x, y + winW, r);
        ctx.arcTo(x, y + winW, x, y, r);
        ctx.arcTo(x, y, x + winH, y, r);
        ctx.fill();
      });
    }
    // doors (outline) – front and rear, both sides
    ctx.strokeStyle = "rgba(60,60,66,0.75)";
    ctx.lineWidth = 2;
    [0.2, 0.71].forEach((v) => {
      winU.forEach((u) => {
        const dh = W * 0.075;
        const dw = H * 0.02;
        ctx.strokeRect(u * W - dh / 2 - W * 0.01, vy(v) - dw / 2, dh, dw);
      });
    });
    // cockpit windows (top-front, u 0.36..0.64 at v≈0.085)
    ctx.fillStyle = "#0E1216";
    const cv = vy(0.088);
    const ch = H * 0.024;
    const panes = [
      [0.375, 0.42],
      [0.428, 0.472],
      [0.48, 0.52],
      [0.528, 0.572],
      [0.58, 0.625],
    ];
    panes.forEach(([a, b]) => {
      ctx.beginPath();
      ctx.moveTo(a * W, cv - ch / 2);
      ctx.lineTo(b * W, cv - ch / 2);
      ctx.lineTo(b * W - 3, cv + ch / 2);
      ctx.lineTo(a * W + 3, cv + ch / 2);
      ctx.closePath();
      ctx.fill();
    });
    // panel lines (very subtle)
    ctx.strokeStyle = "rgba(0,0,0,0.06)";
    ctx.lineWidth = 1;
    for (let v = 0.12; v < 0.95; v += 0.08) {
      ctx.beginPath();
      ctx.moveTo(0, vy(v));
      ctx.lineTo(W, vy(v));
      ctx.stroke();
    }
    // GREINCH titles along both sides (above windows)
    ctx.save();
    ctx.fillStyle = "#6B1F47";
    ctx.font = "bold 30px Montserrat, Arial, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    // right side: text runs from tail (v high) to nose (v low) when read from the outside
    ctx.translate(0.345 * W, vy(0.45));
    ctx.rotate(Math.PI / 2);
    ctx.fillText("GREINCH", 0, 0);
    ctx.restore();
    ctx.save();
    ctx.translate(0.655 * W, vy(0.45));
    ctx.rotate(-Math.PI / 2);
    ctx.fillStyle = "#6B1F47";
    ctx.font = "bold 30px Montserrat, Arial, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("GREINCH", 0, 0);
    ctx.restore();
    // tail logo band
    ctx.fillStyle = "#E94B4B";
    ctx.fillRect(0, vy(1.0), W, vy(0.9) - vy(1.0));
    const t = toTexture(canvas);
    t.wrapT = THREE.ClampToEdgeWrapping;
    return t;
  });
}

/* ============================================================
   Brushed metal (repeatable) – for UFO hull, satellites
   ============================================================ */
export function getBrushedMetalTexture(hex = "#9AA0A8", seed = 21) {
  return memo(`metal-${hex}-${seed}`, () => {
    if (!HAS_DOM) return null;
    const S = 256;
    const canvas = makeCanvas(S, S);
    const ctx = canvas.getContext("2d");
    const base = hexToRgb(hex);
    ctx.fillStyle = `rgb(${base.join(",")})`;
    ctx.fillRect(0, 0, S, S);
    const { rnd } = makeNoise2D(seed);
    for (let i = 0; i < 1400; i++) {
      const y = rnd() * S;
      const k = 0.85 + rnd() * 0.3;
      const c = shade(base, k);
      ctx.strokeStyle = `rgba(${c[0] | 0},${c[1] | 0},${c[2] | 0},0.35)`;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(S, y + (rnd() - 0.5) * 2);
      ctx.stroke();
    }
    // panel seams
    ctx.strokeStyle = "rgba(0,0,0,0.35)";
    ctx.lineWidth = 2;
    for (let i = 0; i < 4; i++) {
      const y = (i + 0.5) * (S / 4);
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(S, y);
      ctx.stroke();
    }
    return toTexture(canvas, { repeat: true });
  });
}

/* ============================================================
   Pre-warm heavy textures in idle time (called once from Scene)
   ============================================================ */
export const PLANET_TYPES = ["lava", "desert", "gas", "ice", "rocky"];

export function prewarmSpaceTextures(airports) {
  if (!HAS_DOM) return;
  const jobs = [];
  airports.forEach((ap, i) => {
    jobs.push(() => getPlanetTextures(PLANET_TYPES[i % PLANET_TYPES.length], ap.color, i + 1));
  });
  jobs.push(() => getEarthTextures());
  jobs.push(() => getCloudTexture());
  jobs.push(() => getSolarTexture());
  jobs.push(() => getBrushedMetalTexture());
  let k = 0;
  const step = () => {
    if (k >= jobs.length) return;
    try {
      jobs[k++]();
    } catch (e) {
      // ignore – textures are optional eye-candy
    }
    if (typeof window !== "undefined" && window.requestIdleCallback) {
      window.requestIdleCallback(step, { timeout: 800 });
    } else {
      setTimeout(step, 120);
    }
  };
  setTimeout(step, 1500);
}
